import React, { useState } from 'react';
import { HeartIcon, ChatBubbleLeftIcon, EllipsisHorizontalIcon, TrashIcon } from '@heroicons/react/24/outline';
import { HeartIcon as HeartIconSolid } from '@heroicons/react/24/solid';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { deleteComment, fetchCommentReplies, fetchCommentRepliesPage, toggleCommentLike, toggleReplyVisibility } from '../../store/slices/portfolioSlice';
import { parseCommentWithMentions } from '../../utils/mentions';
import { PortfolioComment, User } from '../../types';
import toast from 'react-hot-toast';
import Avatar from '../common/Avatar';
import ConfirmationModal from './ConfirmationModal';

interface CommentItemV2Props {
    commentId: string;
    portfolioId: string;
    comment: PortfolioComment;
    isReply?: boolean;
    onReply?: (comment: any) => void;
    onLoadMoreReplies?: (commentId: string) => void;
    allUsers?: any[];
    hasMoreReplies?: boolean;
    repliesLoading?: boolean;
    portfolioOwnerId: string;
    isCompact?: boolean; // For Memory Board compact view
}

const CommentItemV2: React.FC<CommentItemV2Props> = ({
    commentId,
    portfolioId,
    comment,
    isReply = false,
    onReply,
    onLoadMoreReplies,
    allUsers = [],
    hasMoreReplies = false,
    repliesLoading = false,
    portfolioOwnerId,
    isCompact = false
}) => {
  const dispatch = useAppDispatch();
  const { portfolioComments, comments } = useAppSelector(state => state.portfolio);
  const { user } = useAppSelector(state => state.auth);
  const [showDeleteMenu, setShowDeleteMenu] = useState(false);
  const userId = user == null ? "" : user.id

  
  // Get current comment state from Redux store
  const getCurrentComment = () => {

    if (isReply) {
      // Find reply in nested structure
      for (const mainComment of portfolioComments[portfolioId]) {
        if (mainComment.replies) {
          const reply = mainComment.replies.find(r => r.id === commentId);
          if (reply) return reply;
        }
      }
    } else {
      // Find main comment
      return portfolioComments[portfolioId].find(c => c.id === commentId);
    }
    return comment; // Fallback to prop if not found
  };

  const canDeleteComment = user && (
    user.id === comment.user?.id || // Own comment
    user.id === portfolioOwnerId    // Portfolio owner
  );

  const currentComment = getCurrentComment()!;
  const handleToggleLike = async () => {
    try {
      if (comment.is_reply) {
        await dispatch(toggleCommentLike({commentId: currentComment.id, parentCommentId: currentComment.parent_comment_id, isReply: currentComment.is_reply})).unwrap();
      } else {
        await dispatch(toggleCommentLike({commentId: currentComment.id, parentCommentId: currentComment.parent_comment_id, isReply: currentComment.is_reply})).unwrap();
      }
    } catch (error) {
      console.error('Failed to toggle like:', error);
    }
  };

  const handleDeleteComment = () => {
    setShowDeleteMenu(true);
  };

  const confirmDelete = async () => {
    try {
      await dispatch(deleteComment({commentId: comment.id, portfolioId: comment.portfolio_id,})).unwrap();
      toast.success('Comment deleted successfully');
    } catch (error) {
      console.error('Failed to delete comment:', error);
      toast.error('Failed to delete comment');
    }
    setShowDeleteMenu(false);
  };

  const handleOpenUser = (user: User) => {
    window.open(`/profile/${user.id}`, '_blank');
  };

  const handleUserClick = (user: any) => {
    handleOpenUser(user);
  };

  const handleShowReplies = () => {
    if (!currentComment.replies || currentComment.replies.length === 0) {
      // Fetch first page of replies
      dispatch(fetchCommentRepliesPage({ 
        commentId: currentComment.id, 
        page: 1,
        userId: userId 
      }));
    } else {
      dispatch(toggleReplyVisibility({commentId: currentComment.id, portfolioId:currentComment.portfolio_id}));
    }
  };

  const handleLoadMoreReplies = () => {
    if (onLoadMoreReplies && hasMoreReplies) {
      onLoadMoreReplies(currentComment.id);
    }
  };
  const handleMentionClick = (fullName: string) => {
    const [firstName, lastName] = fullName.trim().split(' ');
    let user = null;
    
    if (allUsers.length > 0) {
      user = allUsers.find(u =>
        u.first_name?.toLowerCase() === firstName.toLowerCase() &&
        u.last_name?.toLowerCase() === lastName.toLowerCase()
      );
    }
    
    if (user) handleOpenUser(user);
  };

  const formatTimeAgo = (date: string) => {
    const now = new Date();
    const commentDate = new Date(date);
    const diffInSeconds = Math.floor((now.getTime() - commentDate.getTime()) / 1000);
    
    if (diffInSeconds < 60) return 'just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d`;
    return `${Math.floor(diffInSeconds / 604800)}w`;
  };

  return (
    <div className={`${isReply ? (isCompact ? 'ml-6 py-1' : 'ml-8 py-2') : (isCompact ? 'py-1.5' : 'py-3')} border-b border-gray-100 dark:border-gray-800 last:border-b-0`}>
      <div className={`flex items-start ${isCompact ? 'space-x-2' : 'space-x-3'}`}>
        {/* Avatar */}
        <button
          onClick={() => {
            // console.log("currentComment", currentComment)
            return handleUserClick(currentComment.user)
          }}
          className="flex-shrink-0 hover:opacity-80 transition-opacity"
        >
            <Avatar
              src={currentComment.user?.profile_pic_signed || currentComment.user?.profile_pic}
              alt={currentComment.user?.first_name || ''}
              size={isCompact ? "xs" : "sm"}
              className={`${isCompact ? 'w-6 h-6' : 'w-8 h-8'} ring-1 ring-gray-200 dark:ring-gray-700`}
              isDeleted={!!currentComment.user?.deleted_at || !!currentComment.user?.deletion_scheduled_at}
            />

          {/* <img
            src={currentComment.user?.profile_pic || '/default-avatar.png'}
            alt={`${currentComment.user?.first_name} ${currentComment.user?.last_name}`}
            className="w-8 h-8 rounded-full object-cover ring-1 ring-gray-200"
          /> */}
        </button>

        <div className="flex-1 min-w-0">
          {/* User info - Inline layout */}
          {/* <div className="flex items-center space-x-2 mb-1">
            <button
              onClick={() => handleUserClick(currentComment.user)}
              className="font-semibold text-gray-900 hover:text-blue-600 transition-colors text-sm"
            >
              {currentComment.user?.first_name} {currentComment.user?.last_name}
            </button>
            
            {/* Professional badge *//*}
            {currentComment.user?.user_type && currentComment.user.user_type !== 'user' && (
              <span className="text-xs text-blue-600 font-medium capitalize">
                ({currentComment.user.user_type})
              </span>
            )}
            
            <span className="text-xs text-gray-500">
              {formatTimeAgo(currentComment.created_at)}
            </span>
          </div> */}
          <div className="flex items-center justify-between mb-1">
          <div className="flex items-center space-x-2 min-w-0 flex-1 mr-2">
    <button
      onClick={() => handleUserClick(currentComment.user)}
      className={`font-semibold text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400 transition-colors ${isCompact ? 'text-xs' : 'text-sm'} truncate max-w-[120px]`}
      title={`${currentComment.user?.first_name} ${currentComment.user?.last_name}`}
    >
      {currentComment.user?.first_name} {currentComment.user?.last_name}
    </button>
    
    {/* Professional badge
    {comment.user?.user_type && comment.user.user_type !== 'user' && (
      <span 
        className="text-xs text-blue-600 font-medium capitalize truncate max-w-[80px]"
        title={comment.user.user_type}
      >
        ({comment.user.user_type})
      </span>
    )} */}
    
    <span className={`${isCompact ? 'text-[10px]' : 'text-xs'} text-gray-500 dark:text-gray-400 whitespace-nowrap`}>
      {formatTimeAgo(currentComment.created_at)}
    </span>
  </div>

            {/* Right side - Like button and delete menu */}
            <div className="flex items-center space-x-2">
            {canDeleteComment &&
              <button
                onClick={handleDeleteComment}
                className="p-1.5 text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                title="Delete comment"
              >
                <TrashIcon className="w-4 h-4" />
              </button>
            }
              {/* Like button - moved to right */}
              <button
                onClick={handleToggleLike}
                className={`flex items-center space-x-1 transition-colors hover:text-red-500 dark:hover:text-red-400 ${
                  currentComment.has_liked ? 'text-red-500' : 'text-gray-500 dark:text-gray-400'
                }`}
              >
                {currentComment.has_liked ? (
                  <HeartIconSolid className="w-4 h-4" />
                ) : (
                  <HeartIcon className="w-4 h-4" />
                )}
                <span className="text-xs font-medium">{currentComment.like_count || 0}</span>
              </button>

              {/* Delete menu for own comments or portfolio owner */}
              {/* {canDeleteComment && (
                <div className="relative">
                  <button
                    onClick={() => setShowDeleteMenu(!showDeleteMenu)}
                    className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                  >
                    <EllipsisHorizontalIcon className="w-4 h-4 text-gray-500" />
                  </button>

                  {/* Delete dropdown menu *//*}
                  {showDeleteMenu && (
                    <div className="absolute right-0 mt-1 w-32 bg-white rounded-lg shadow-lg border border-gray-200 z-10">
                      <button
                        onClick={handleDeleteComment}
                        className="flex items-center w-full px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg"
                      >
                        <TrashIcon className="w-4 h-4 mr-2" />
                        Delete
                      </button>
                    </div>
                  )}
                </div>
              )} */}
            </div>
          </div>

          {/* Comment content */}
          <div className={`${isCompact ? 'text-xs' : 'text-sm'} text-gray-700 dark:text-gray-300 leading-relaxed ${isCompact ? 'mb-1' : 'mb-2'}`}>
            {parseCommentWithMentions(currentComment.content, handleMentionClick)}
          </div>

          {/* Actions */}
          <div className="flex items-center space-x-4 text-xs text-gray-500 dark:text-gray-400">
            {/* Like button - Use current state from Redux
            <button
              onClick={handleToggleLike}
              className={`flex items-center space-x-1 transition-colors hover:text-red-500 ${
                currentComment.has_liked ? 'text-red-500' : ''
              }`}
            >
              {currentComment.has_liked ? (
                <HeartIconSolid className="w-4 h-4" />
              ) : (
                <HeartIcon className="w-4 h-4" />
              )}
              <span className="font-medium">{currentComment.like_count || 0}</span>
            </button> */}

            {/* Reply button */}
            {onReply && (
              <button
                onClick={() => onReply(currentComment)}
                className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors font-medium"
              >
                Reply
              </button>
            )}

            {/* Show replies */}
            {!isReply && currentComment.reply_count > 0 && (
              <button
                onClick={handleShowReplies}
                className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors font-medium"
              >
                {currentComment.show_replies ? 'Hide' : `Show ${currentComment.reply_count}`} 
                {currentComment.reply_count === 1 ? ' reply' : ' replies'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Nested replies */}
      {currentComment.show_replies && currentComment.replies && currentComment.replies.length > 0 && (
        <div className="mt-2">
          {currentComment.replies.map((reply: any) => (
            <CommentItemV2
              commentId={reply.id}
              portfolioId={reply.portfolio_id}
              key={reply.id}
              comment={reply}
              isReply={true}
              onReply={onReply}
              allUsers={allUsers}
              portfolioOwnerId={portfolioOwnerId}
              isCompact={isCompact}
            />
          ))}

          {/* Load More Replies Button */}
          {hasMoreReplies && (
            <div className="ml-8 mt-2">
              <button
                onClick={handleLoadMoreReplies}
                disabled={repliesLoading}
                className="text-blue-600 hover:text-blue-700 font-medium text-xs disabled:opacity-50"
              >
                {repliesLoading ? 'Loading...' : 'Load more replies'}
              </button>
            </div>
          )}
        </div>
      )}
      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteMenu}
        onClose={() => setShowDeleteMenu(false)}
        onConfirm={confirmDelete}
        title="Delete Comment"
        message="Are you sure you want to delete this comment? This action cannot be undone."
        confirmText="Delete"
        type="danger"
      />
    </div>
  );
};

export default CommentItemV2;
